import { Component, HostListener, Input } from '@angular/core';
import { News, Organisation, Event } from '../../../models/types';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NewsListComponent } from '../news-list/news-list.component';
import { LoginService } from '../../services/login.service';
import { OrganisationServiceService } from '../../services/organisation-service.service';
import { EventService } from '../../services/event.service';
import { imageLocation } from '../../../environments/environment.development';
import { GalleryImage, GalleryItemEvent } from '@daelmaak/ngx-gallery';
import { ReportListComponent } from "../report-list/report-list.component";
import { GalleryModule } from '@daelmaak/ngx-gallery';
import Swal from 'sweetalert2';
import { HttpResponse } from '@angular/common/http';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTrash, faPlus, faCalendar, faHouse, faEnvelope, faPhone, faEnvelopeOpenText, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { ProfilePageService } from '../../services/profile-page.service';
import * as L from 'leaflet';

@Component({
  selector: 'app-church-page',
  standalone: true,
  imports: [
    CommonModule,
    NewsListComponent,
    NgOptimizedImage, 
    ReportListComponent, 
    GalleryModule,
    FontAwesomeModule
  ],
  templateUrl: './church-page.component.html',
  styleUrl: './church-page.component.scss'
})
export class ChurchPageComponent {
  @Input() organisation: Organisation | null = null; 
  @Input() isSuperAdmin: boolean = false;

  menuOpen: boolean = false;
  totalItems: number = 0;
  organisationId: string = '';
  news: News[] = [];
  allEvents: Event[] = [];
  isOrgAdmin: boolean = false;
  isLoading: boolean = false;
  currentPage: number = 1;
  pageSize: number = 4;
  error: string | null = null;
  churchImages: GalleryImage[] = [];
  selectedImageIndex: number = -1;
  imageSelected: boolean = false;

  faTrash = faTrash;
  faPlus = faPlus;
  faHouse = faHouse;
  faEnvelopeOpenText = faEnvelopeOpenText
  faMapMarkerAlt = faMapMarkerAlt;
  faPhone = faPhone;

  constructor(
    private loginService: LoginService,
    private organisationService: OrganisationServiceService,
    private eventService: EventService,
    private profilePageService: ProfilePageService
  ) {}

  ngOnInit() {
    console.log('ChurchPageComponent initialized with organisation:', this.organisation);
    this.isOrgAdmin = this.loginService.IsOrgAdminLoggedIn("church");
    this.extractOrganisationIdFromName();
    this.handleScroll();
  }

  ngAfterViewInit(): void {
    const map = L.map('map').setView([45.9300, 14.14148], 15); // Hotedršica coordinates

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);

    const marker = L.marker([45.9300, 14.14148]).addTo(map);
    marker.bindPopup('<b>Župnijska cerkev sv. Janeza Krstnika</b><br>Hotedršica 63').openPopup();
  }

  extractOrganisationIdFromName() {
    this.organisationService.getOrganisationIdByName("Župnija Hotedršica").subscribe({
      next: (response) => {
        this.organisationId = response.body?.organisationID.toString() || '';
        this.loadEvents();
        this.getNews();
        this.fetchOrgImages();
      },
      error: (error) => {
        console.error('Error fetching organisation ID:', error);
      }
    });
  }

  loadEvents() {
    this.eventService.getAllEvents().subscribe((response) => {
      this.allEvents = response.events!.filter((event: Event) => event.organisationID === Number(this.organisationId));
    });
  }

  fetchOrgImages() {
    this.organisationService.getOrganisationImages(this.organisation!.organisationID).subscribe({
      next: (response) => {
        this.churchImages = response.body?.images.map((image: any) => {
          return new GalleryImage(
            `${imageLocation}/static/content/organizations/${this.organisationId}/${image}`,
            `${imageLocation}/static/content/organizations/${this.organisationId}/${image}`,
          );
        }) || []; // Fallback to empty array if response.body is null
      },
      error: (err) => {
        console.error('Failed to fetch organisation images:', err);
      }
    });
  } 

  getNews() {
    this.isLoading = true;

    this.organisationService.getPaginatedNews(this.currentPage, this.pageSize, this.organisationId).subscribe({
      next: (response) => {
        if (response && Array.isArray(response.news)) {
          const newNews = this.assignNewsImages(response.news);
          this.news = [...this.news, ...newNews]; // Append, don't replace
          this.totalItems = response.total;
        } else {
          this.news = [];
        }

        this.isLoading = false;
      },
      error: (err) => {
        this.error = 'Failed to load news.';
        this.isLoading = false;
      }
    });
  }
  
  handleEventAdded() {
    this.allEvents = [];
    this.currentPage = 1;
    this.loadEvents();
  }

  handleNewsChanged() {
    this.news = [];
    this.currentPage = 1;
    this.getNews();
  }


  loadMoreNews() {
    this.currentPage++;
    this.getNews(); // will use the updated page
  }


  private assignNewsImages(news: News[]): News[] {
    return news.map(newsItem => {
      let defaultImageUrl = '../../../assets/images/generic/news.png';
      newsItem.imageRef = newsItem.imageRef ?
      `${imageLocation}/static/content/news/${newsItem.newsID}/${newsItem.imageRef}` :
      defaultImageUrl;
      return newsItem;
    })
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  scrollTo(sectionId: string) {
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 100;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset;
  
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
      this.menuOpen = false;
    }, 50);
  }

    @HostListener('window:scroll', [])
    handleScroll() {
      const images = document.querySelectorAll('.animated-image') as NodeListOf<HTMLElement>;
      const titles = document.querySelectorAll('.animated-title') as NodeListOf<HTMLElement>;
      const texts = document.querySelectorAll('.animated-text') as NodeListOf<HTMLElement>;
  
      this.animateOnScroll(images, 'left-image', 'slide-in-left');
      this.animateOnScroll(images, 'right-image', 'slide-in-right');
      this.animateOnScroll(titles, '', 'slide-in-left'); // No specific class, so we just add the animation
      this.animateOnScroll(texts, '', 'slide-in-right'); // No specific class, just add the animation
    }

  private animateOnScroll(elements: NodeListOf<HTMLElement>, className: string, animationClass: string) {
    elements.forEach(element => {
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        if (className === '' || element.classList.contains(className)) {
          element.classList.add(animationClass);
        }
      }
    });
  }

deleteSelectedImage() {
    if (this.selectedImageIndex !== null) {
      Swal.fire({
        title: 'Ali želite nadaljevati?',
        text: 'Ali želite izbrisati sliko?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Da, izbriši!',
        cancelButtonText: 'Ne, prekliči!'
      }).then((result) => {
        if (result.isConfirmed) {
          const imageToDelete = this.churchImages[this.selectedImageIndex];
          const pictureSrc = imageToDelete.src;
  
          this.organisationService.deleteOrgImage(Number(this.organisationId), pictureSrc).subscribe({
            next: (response: HttpResponse<any>) => {
              console.log('Image deleted successfully:', response);
              this.churchImages.splice(this.selectedImageIndex, 1); // Remove image from the gallery
              this.selectedImageIndex = -1;
              this.imageSelected = false;
            },
            error: (err) => {
              console.error('Failed to delete image:', err);
            }
          });
        } else {
          console.log('Image deletion cancelled');
        }
      });
    } else {
      console.log('No image selected for deletion');
    }
  }

  onThumbClick(event: GalleryItemEvent) {
    this.selectedImageIndex = event.index;
    this.imageSelected = true;
    console.log(`Selected image index: ${this.selectedImageIndex}`);
  }

    openAddSportsImagesModal() {
    this.profilePageService.openOrgImageAddModal(this.organisation!);  
  }

} 

